import { ChangeEvent, InputHTMLAttributes, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import CurrencyInput from 'react-currency-input-field'
import cl from 'classnames'
import { Label } from './label'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  variant?: 'default' | 'currency' | 'mask'
  name: string
  labelText: string
  mask?: string
  className?: string
  maxCurrency?: number
  isError?: boolean
  errorMessage?: string
  onChange?: (event: ChangeEvent<HTMLInputElement>, value?: number) => void
}

const applyMask = (value: string, mask: string): string => {
  const cleanValue = value.replace(/\D/g, '')
  let maskedValue = ''
  let valueIndex = 0

  for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
    if (mask[i] === '9') {
      maskedValue += cleanValue[valueIndex]
      valueIndex++
    } else {
      maskedValue += mask[i]
    }
  }

  return maskedValue
}

export const Input = ({
  variant = 'default',
  name,
  labelText,
  mask,
  className = '',
  maxCurrency,
  isError,
  errorMessage,
  onChange,
  ...rest
}: InputProps) => {
  const { register, setValue } = useFormContext()
  const [displayValue, setDisplayValue] = useState('')

  const inputStyle = cl(
    'font-myriad w-full h-[32px] px-4 pt-[8px] pb-[6px] text-base leading-none border border-v3-gray-250 rounded-4xl lg:h-[32px] lg:text-xs lg:leading-none placeholder:uppercase placeholder:text-v3-gray-250 disabled:bg-[#E3E3E3] disabled:text-[#0000004D] disabled:border-v3-gray-250',
    {
      'border-v3-red-400 placeholder:text-v3-red-400': isError
    }
  )

  return (
    <div className={`relative flex flex-col ${className}`}>
      <Label htmlFor={name}>{labelText}</Label>

      {variant === 'default' && (
        <input id={name} className={inputStyle} {...register(name)} {...rest} />
      )}

      {variant === 'mask' && (
        <input
          id={name}
          type="text"
          className={inputStyle}
          placeholder={rest.placeholder as string}
          disabled={rest.disabled}
          value={displayValue}
          onChange={e => {
            const rawValue = e.target.value.replace(/\D/g, '')
            const masked = applyMask(e.target.value, mask)

            setDisplayValue(masked)
            setValue(name, rawValue)

            if (onChange) {
              onChange({ target: { name, value: rawValue } } as any)
            }
          }}
        />
      )}

      {variant === 'currency' && (
        <CurrencyInput
          id={name}
          name={name}
          className={inputStyle}
          placeholder="R$ 0,00"
          decimalsLimit={2}
          decimalSeparator=","
          groupSeparator="."
          prefix="R$ "
          max={maxCurrency}
          disabled={rest.disabled}
          onValueChange={value => {
            const numValue = value ? parseFloat(value) : undefined
            setValue(name, numValue)
            if (onChange) {
              onChange(
                {
                  target: { name, value: numValue }
                } as any,
                numValue
              )
            }
          }}
        />
      )}

      {isError && errorMessage && (
        <p className="absolute -bottom-4 ml-2 text-[10px] text-v3-red-400">
          {errorMessage}
        </p>
      )}
    </div>
  )
}
